import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";

/**
 * Phase 14.3 — shown on the dashboard when a user has completed onboarding
 * but has no instantiated training plan yet.
 *
 * Replaces the old `nav("/setup")` redirect, which looped:
 *   / -> plan_instantiated=false -> /setup -> /onboarding -> already
 *   onboarded -> / -> ... -> blank page.
 *
 * One click POSTs /plan/instantiate, then invalidates the profile +
 * dashboard queries so the dashboard re-renders fully populated. New users
 * normally never see this — onboarding instantiates the plan on completion;
 * this is the safety net for the rare onboarded-but-no-plan case.
 */
export function EmptyPlanCTA() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/plan/instantiate");
      await qc.invalidateQueries({ queryKey: ["profile"] });
      await qc.invalidateQueries({ queryKey: ["dashboard"] });
      // Leave busy=true — the invalidation flips plan_instantiated, the
      // Dashboard re-renders its real content, and this component unmounts.
    } catch {
      setError(t("dashboard.empty_state.error"));
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-md py-12">
      <Card>
        <SectionLabel>{t("dashboard.empty_state.label")}</SectionLabel>
        <h2 className="mt-2 font-serif text-[22px] font-medium leading-tight text-navy-deep">
          {t("dashboard.empty_state.title")}
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-taupe">
          {t("dashboard.empty_state.body")}
        </p>
        {error ? (
          <div className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
            {error}
          </div>
        ) : null}
        <Button
          className="mt-5 w-full"
          size="md"
          loading={busy}
          onClick={handleCreate}
        >
          {t("dashboard.empty_state.create_button")}
        </Button>
      </Card>
    </div>
  );
}
